import { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button as MuiButton } from "@mui/material";
import { toast } from "sonner";
import { useAuthStore } from "@/modules/auth";
import { api, toastApiError } from "@/api/client";
import { Clock } from "@/shared/icons";

function formatRemaining(seconds: number): string {
  if (seconds <= 0) return "0s";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export function SessionTimer() {
  // Pill anchored to the absolute session cap returned by the server. Silent
  // refresh-token rotation does NOT reset this — only a re-login or an
  // explicit Extend call does. JWT `exp` is not read here.
  const sessionExpiresAt = useAuthStore((s) => s.sessionExpiresAt);
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const [dismissedExp, setDismissedExp] = useState<number | null>(null);
  const [extending, setExtending] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);

  const exp = sessionExpiresAt
    ? Math.floor(new Date(sessionExpiresAt).getTime() / 1000)
    : null;
  if (!exp) return null;

  const remaining = exp - now;
  const open = remaining > 0 && remaining <= 300 && dismissedExp !== exp;

  const extend = async () => {
    setExtending(true);
    try {
      // Dedicated endpoint — /auth/refresh only rotates the token and keeps
      // the absolute cap; extend-session re-anchors the cap to now.
      const r = await api.post<{
        accessToken: string;
        sessionExpiresAt?: string | null;
      }>("/auth/extend-session");
      setAccessToken(r.data.accessToken, r.data.sessionExpiresAt ?? null);
      setDismissedExp(null);
      toast.success("Session extended");
    } catch (e: unknown) {
      toastApiError(e, "Could not extend session — please log in again");
    } finally {
      setExtending(false);
    }
  };

  // Pulsing pill — visual urgency ramps as token nears expiry.
  // <= 5 min → danger (red), pulse fast; <= 30 min → warning (amber), pulse slow; else neutral accent.
  const danger = remaining > 0 && remaining <= 300;
  const warning = !danger && remaining > 0 && remaining <= 1800;
  const accent = danger
    ? "var(--danger-700, #b91c1c)"
    : warning
      ? "var(--warning-700, #b45309)"
      : "var(--accent-700, #1d4ed8)";
  const bg = danger
    ? "var(--danger-50, #fef2f2)"
    : warning
      ? "var(--warning-50, #fffbeb)"
      : "var(--accent-50, #eff6ff)";
  const ring = danger
    ? "var(--danger-300, #fca5a5)"
    : warning
      ? "var(--warning-300, #fcd34d)"
      : "var(--accent-300, #93c5fd)";
  const pulseSpeed = danger ? "1s" : warning ? "1.8s" : "2.6s";

  return (
    <>
      <style>{`
        @keyframes wmSessionPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(1.4); }
        }
      `}</style>
      <div
        className="hidden items-center gap-2 rounded-full px-2.5 py-1 text-[12px] font-medium md:inline-flex"
        title={`Log session ends in ${formatRemaining(remaining)}`}
        style={{
          background: bg,
          border: `1px solid ${ring}`,
          color: accent,
        }}
      >
        <span
          aria-hidden
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: accent,
            animation: `wmSessionPulse ${pulseSpeed} ease-in-out infinite`,
            display: "inline-block",
          }}
        />
        <Clock size={13} />
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            opacity: 0.85,
          }}
        >
          Log session ends in
        </span>
        <span
          className="tnum"
          style={{ fontVariantNumeric: "tabular-nums", letterSpacing: 0.2 }}
        >
          {formatRemaining(remaining)}
        </span>
      </div>

      <Dialog open={open} onClose={() => setDismissedExp(exp)} maxWidth="xs" fullWidth>
        <DialogTitle>Session expiring soon</DialogTitle>
        <DialogContent>
          <p className="text-sm text-[var(--ink-2)]">
            Your session will expire in <strong>{formatRemaining(remaining)}</strong>. Extend now to
            stay signed in.
          </p>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setDismissedExp(exp)}>Dismiss</MuiButton>
          <MuiButton variant="contained" onClick={extend} disabled={extending}>
            {extending ? "Extending..." : "Extend session"}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
